import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import EditNoteIcon from '@mui/icons-material/EditNote';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CircularProgress from '@mui/material/CircularProgress';
import { AppPagination } from '../../ui/AppPagination';

interface DraftsListPageProps {
    onLogout: () => void;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const PAGE_SIZE = 10;

const DraftsListPage: React.FC<DraftsListPageProps> = ({ onLogout }) => {
    const [drafts, setDrafts] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    const loadDrafts = async (currentPage: number) => {
        setLoading(true);
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${API_BASE_URL}/api/v1/application-drafts/?page=${currentPage}&size=${PAGE_SIZE}`, {
                headers: { "Authorization": token || '' }
            });

            if (res.status === 401) {
                onLogout();
                return;
            }

            if (res.ok) {
                const data = await res.json();
                setDrafts(data.items || []);
                setTotalPages(data.pages || 1);
                setTotalItems(data.total || 0);
            }
        } catch (e) {
            console.error("Ошибка загрузки черновиков", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadDrafts(page);
    }, [page]);

    const handleDelete = async (id: number) => {
        if (!window.confirm("Удалить этот черновик?")) return;
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${API_BASE_URL}/api/v1/application-drafts/${id}`, {
                method: "DELETE",
                headers: { "Authorization": token || '' }
            });

            if (res.status === 401) {
                onLogout();
                return;
            }

            if (res.ok) {
                loadDrafts(page);
            }
        } catch (e) {
            console.error("Ошибка удаления", e);
        }
    };

    const formatDateInMoscow = (isoString: string) => {
        if (!isoString) return '—';

        try {
            const utcString = isoString.endsWith('Z') ? isoString : `${isoString}Z`;

            return new Date(utcString).toLocaleString('ru-RU', {
                timeZone: 'Europe/Moscow',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (e) {
            return isoString;
        }
    };

    return (
        <div className="w-full">
            <div className="p-4 lg:p-8 space-y-8">
                <h1 className="text-2xl font-semibold text-gray-800 flex items-center gap-3">
                    <EditNoteIcon fontSize="large" className="text-blue-600" />
                    Черновики заявок
                </h1>

                <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto">
                    {loading ? (
                        <div className="flex justify-center items-center py-12">
                            <CircularProgress />
                        </div>
                    ) : drafts.length === 0 ? (
                        <div className="p-12 text-center text-gray-500">
                            У вас пока нет сохраненных черновиков.
                        </div>
                    ) : (
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Организация</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Специалист</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Папка</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Студентов</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Дата обновления</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Действия</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {drafts.map((draft) => (
                                    <tr key={draft.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            #{draft.id}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500 max-w-[200px] whitespace-normal break-words">
                                            {draft.data?.ui_state?.organization?.name || "—"}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500 max-w-[180px] whitespace-normal break-words">
                                            {draft.data?.ui_state?.specialist?.fullName || "—"}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500 max-w-[120px] whitespace-normal break-words">
                                            {draft.data?.ui_state?.subfolderName || "—"}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                            {draft.data?.ui_state?.students?.length || 0} чел.
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDateInMoscow(draft.updated_at)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex items-center space-x-2">
                                                <Link
                                                    to={`/drafts/${draft.id}`}
                                                    className="p-2 text-gray-400 hover:text-blue-600 rounded-full hover:bg-gray-100 transition"
                                                    title="Продолжить редактирование"
                                                >
                                                    <EditIcon sx={{ fontSize: 20 }} />
                                                </Link>
                                                <button
                                                    onClick={() => handleDelete(draft.id)}
                                                    className="p-2 text-gray-400 hover:text-red-600 rounded-full hover:bg-gray-100 transition"
                                                    title="Удалить черновик"
                                                >
                                                    <DeleteIcon sx={{ fontSize: 20 }} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                {totalPages > 1 && (
                    <AppPagination
                        page={page}
                        totalItems={totalItems}
                        pageSize={PAGE_SIZE}
                        onChange={setPage}
                    />
                )}
            </div>
        </div>
    );
};

export default DraftsListPage;